import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { paymentVoucherService } from "services";
import {
  PaymentVoucherForm,
  PaymentVoucherQuery,
  PaymentVoucherUpdatePayload,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type PaymentVoucherListKey =
  | ["payment-vouchers"]
  | ["payment-vouchers", PaymentVoucherQuery];
type PaymentVoucherDetailKey = ["payment-vouchers", "detail", number | string];

const paymentVoucherKeys = {
  all: ["payment-vouchers"] as const,
  list: (params?: PaymentVoucherQuery): PaymentVoucherListKey =>
    params ? ["payment-vouchers", params] : ["payment-vouchers"],
  detail: (id: number | string): PaymentVoucherDetailKey => [
    "payment-vouchers",
    "detail",
    id,
  ],
};

export function usePaymentVoucher() {
  const queryClient = useQueryClient();

  const fetchList = (params?: PaymentVoucherQuery, enabled = true) => {
    return useQuery({
      queryKey: paymentVoucherKeys.list(params),
      queryFn: () => paymentVoucherService.list(params),
      enabled,
      select: (data) => ({
        data: data?.data ?? [],
        totalPage: Math.max(1, data?.totalPage ?? 1),
      }),
    });
  };

  const fetchDetail = (id: number | string, enabled = true) => {
    return useQuery({
      queryKey: paymentVoucherKeys.detail(id),
      queryFn: () => paymentVoucherService.show(id),
      enabled: Boolean(id) && enabled,
    });
  };

  const createPaymentVoucher = useMutation({
    mutationFn: (payload: PaymentVoucherForm) =>
      paymentVoucherService.store(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200 || res?.statusCode === 201) {
        SnackbarUtils.success(res?.message ?? "Tạo phiếu chi thành công");
      }
      queryClient.invalidateQueries({ queryKey: paymentVoucherKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Tạo phiếu chi thất bại");
      console.error("createPaymentVoucher failed", err);
    },
  });

  const updatePaymentVoucher = useMutation({
    mutationFn: (payload: PaymentVoucherUpdatePayload) =>
      paymentVoucherService.update(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Cập nhật phiếu chi thành công");
      }
      queryClient.invalidateQueries({ queryKey: paymentVoucherKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Cập nhật phiếu chi thất bại");
      console.error("updatePaymentVoucher failed", err);
    },
  });

  const deletePaymentVoucher = useMutation({
    mutationFn: (id: number | string) => paymentVoucherService.destroy(id),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Xoá phiếu chi thành công");
      }
      queryClient.invalidateQueries({ queryKey: paymentVoucherKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Xoá phiếu chi thất bại");
      console.error("deletePaymentVoucher failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createPaymentVoucher,
    updatePaymentVoucher,
    deletePaymentVoucher,
  };
}
